import { useState, useCallback } from 'react';
import { isNativePlatform } from '../lib/capacitor';
import { Geolocation } from '@capacitor/geolocation';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse';

export default function useGeolocation() {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getLocation = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let latitude, longitude;

      if (isNativePlatform()) {
        // Request permissions first on native (required on iOS)
        await Geolocation.requestPermissions();
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } else {
        // Web fallback
        if (!navigator.geolocation) {
          setError('Geolocation not supported');
          setLoading(false);
          return null;
        }

        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000,
          });
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      }

      // Try to get location name via reverse geocoding
      let locationName = null;
      try {
        const response = await fetch(
          `${NOMINATIM_URL}?lat=${latitude}&lon=${longitude}&format=json`,
          { headers: { 'User-Agent': 'WiredCop-TLC-Checker' } }
        );
        const data = await response.json();

        // Build a short, useful location name
        const addr = data.address || {};
        const parts = [];
        if (addr.neighbourhood || addr.suburb) parts.push(addr.neighbourhood || addr.suburb);
        if (addr.city || addr.town || addr.village) parts.push(addr.city || addr.town || addr.village);
        if (parts.length === 0 && addr.county) parts.push(addr.county);

        locationName = parts.join(', ') || data.display_name?.split(',').slice(0, 2).join(',');
      } catch (geoErr) {
        // Continue without location name
      }

      const loc = { latitude, longitude, locationName };
      setLocation(loc);
      setLoading(false);
      return loc;
    } catch (err) {
      const errorMsg = err.code === 1 ? 'Location permission denied' :
                       err.code === 2 ? 'Location unavailable' :
                       err.code === 3 ? 'Location request timed out' :
                       err.message || 'Could not get location';
      setError(errorMsg);
      setLoading(false);
      return null;
    }
  }, []);

  const clearLocation = useCallback(() => {
    setLocation(null);
    setError(null);
  }, []);

  return { location, loading, error, getLocation, clearLocation };
}
